import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detection Lab -- internal page for testing the camera/screen identifier
// (backend/visual_identify.py). Upload 1-5 frames of the same screen and see
// exactly what the pipeline saw and why it chose what it chose. Marking the
// right answer records the outcome, which feeds the calibration table below
// (the same data real user confirmations produce).

// Downscale to <=1280px JPEG in the browser: keeps uploads small and matches
// what the phone will send.
function toJpegBase64(file, maxSide = 1280) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(img.src);
      resolve(canvas.toDataURL('image/jpeg', 0.85).split(',')[1]);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

const DECISION_COPY = {
  auto: { label: 'Identified', cls: 'bg-emerald-500/20 text-emerald-300' },
  candidates: { label: 'Asks: is it one of these?', cls: 'bg-amber-500/20 text-amber-300' },
  insufficient: { label: 'Not sure yet (asks for another look)', cls: 'bg-neutral-500/20 text-neutral-300' },
  not_content: { label: 'Not a show (menu / ad / trailer)', cls: 'bg-neutral-500/20 text-neutral-300' },
  sports: { label: 'Sports', cls: 'bg-sky-500/20 text-sky-300' },
};

export default function DetectionLab() {
  const { getToken } = useAuth();
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [hint, setHint] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [calibration, setCalibration] = useState(null);
  const [labeled, setLabeled] = useState(null);

  const loadCalibration = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/detection/visual/calibration`, { headers: { Authorization: `Bearer ${token}` } });
      setCalibration(res.data);
    } catch {}
  }, [getToken]);

  useEffect(() => { loadCalibration(); }, [loadCalibration]);

  useEffect(() => {
    const urls = files.map(f => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [files]);

  const run = async () => {
    if (!files.length) return;
    setRunning(true);
    setResult(null);
    setLabeled(null);
    try {
      const frames = await Promise.all(files.slice(0, 5).map(f => toJpegBase64(f)));
      const token = await getToken();
      const res = await axios.post(`${API}/detection/visual/identify`, { frames, service_hint: hint },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Identification failed');
    } finally {
      setRunning(false);
    }
  };

  const label = async (cand) => {
    try {
      const token = await getToken();
      const body = cand
        ? { scan_id: result.scan_id, tmdb_id: cand.tmdb_id, media_type: cand.media_type, title: cand.title, season: cand.season, episode: cand.episode }
        : { scan_id: result.scan_id };
      const res = await axios.post(`${API}/detection/visual/confirm`, body, { headers: { Authorization: `Bearer ${token}` } });
      setLabeled(res.data.outcome);
      toast.success(cand ? `Recorded: ${cand.title}` : 'Recorded: none of these');
      loadCalibration();
    } catch { toast.error('Could not record'); }
  };

  const d = result && (DECISION_COPY[result.decision] || { label: result.decision, cls: '' });

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 text-white">
      <div>
        <h1 className="text-2xl font-black">Detection Lab</h1>
        <p className="text-sm text-neutral-400 mt-1">
          Upload 1–5 photos or screenshots of the same screen. Images are processed and discarded, never stored.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3">
        <input type="file" accept="image/*" multiple
          onChange={e => setFiles(Array.from(e.target.files || []).slice(0, 5))}
          className="block w-full text-sm text-neutral-300 file:mr-3 file:rounded-full file:border-0 file:bg-violet-600 file:px-4 file:py-2 file:text-white" />
        {previews.length > 0 && (
          <div className="flex gap-2 overflow-x-auto">
            {previews.map(u => <img key={u} src={u} alt="" className="h-24 rounded-lg object-cover" />)}
          </div>
        )}
        <input value={hint} onChange={e => setHint(e.target.value)} placeholder="Optional: app the TV says is open (e.g. Hulu)"
          className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm" />
        <button onClick={run} disabled={!files.length || running}
          className="px-5 py-2 rounded-full font-bold text-sm disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' }}>
          {running ? 'Reading…' : 'Identify'}
        </button>
      </div>

      {result && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${d.cls}`}>{d.label}</span>
            <span className="text-xs text-neutral-400">
              {result.content_type} · frame quality {result.frame_quality}{result.service_ui ? ` · ${result.service_ui} UI` : ''}
            </span>
          </div>
          <div className="text-xs text-neutral-400">
            {result.models.join(' → ')} · {(result.latency_ms / 1000).toFixed(1)}s · ${result.cost_usd.toFixed(4)} · {result.pipeline_version}
          </div>

          {result.sports && (
            <div className="text-sm">
              {result.sports.league} · {result.sports.teams.join(' vs ')} {result.sports.network ? `on ${result.sports.network}` : ''} {result.sports.score_text || ''}
            </div>
          )}

          {result.candidates.length > 0 && (
            <div className="space-y-2">
              {result.candidates.map((c, i) => (
                <div key={c.key} className="flex gap-3 items-start rounded-xl bg-black/30 p-3">
                  {c.poster_url ? <img src={c.poster_url} alt="" className="w-12 h-[72px] rounded object-cover" /> : <div className="w-12 h-[72px] rounded bg-white/10" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold truncate">{i + 1}. {c.title}</span>
                      <span className="text-xs text-neutral-500">{c.media_type}{c.season ? ` · S${c.season}${c.episode ? `E${c.episode}` : ''}${c.episode_verified ? ' ✓' : ''}` : ''}</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-violet-500" style={{ width: `${Math.round(c.score * 100)}%` }} />
                    </div>
                    <div className="text-xs text-neutral-400 mt-1">score {c.score.toFixed(2)} · {c.reasons.join(' · ')}</div>
                  </div>
                  {!labeled && (
                    <button onClick={() => label(c)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10">
                      Correct
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!labeled ? (
            <button onClick={() => label(null)} className="text-xs text-neutral-400 underline">None of these is right</button>
          ) : (
            <p className="text-xs text-emerald-300">Outcome recorded: {labeled}</p>
          )}

          {result.visible_text.length > 0 && (
            <details className="text-xs text-neutral-400">
              <summary className="cursor-pointer">On-screen text the model read ({result.visible_text.length})</summary>
              <ul className="mt-2 space-y-0.5 font-mono">{result.visible_text.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </details>
          )}
        </div>
      )}

      {calibration && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm space-y-2">
          <p className="font-bold">Your labeled scans: {calibration.labeled_scans}</p>
          {calibration.labeled_scans > 0 && (
            <p className="text-neutral-300">
              Top-1 {fmtPct(calibration.top1_accuracy)} · Top-3 {fmtPct(calibration.top3_accuracy)} · Wrong when sure {fmtPct(calibration.auto_false_positive_rate)}
            </p>
          )}
          {calibration.note && <p className="text-xs text-amber-300/80">{calibration.note}</p>}
          {calibration.by_score.length > 0 && (
            <table className="text-xs text-neutral-300">
              <thead><tr className="text-neutral-500"><th className="pr-4 text-left">Score</th><th className="pr-4 text-left">Scans</th><th className="text-left">Actually right</th></tr></thead>
              <tbody>{calibration.by_score.map(b => (
                <tr key={b.bucket}><td className="pr-4">{b.bucket}</td><td className="pr-4">{b.n}</td><td>{fmtPct(b.top1_accuracy)}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function fmtPct(v) {
  return v == null ? '—' : `${Math.round(v * 100)}%`;
}
